/*    
<summary>
  This functional component "AddProductManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import Popover from "@mui/material/Popover";
import { RichTreeView } from "@mui/x-tree-view";
import dayjs from "dayjs";
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikProps,
  useFormikContext,
} from "formik";
import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import {
  DATE_FORMAT,
  integerNumJpRegex,
  integerRegex,
  stringJpRegex,
} from "../../../../constants/constants";
import { useStore } from "../../../../contexts/store-provider";
import { IOption } from "../../../../models/ICommon";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikDateFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";
import Select, { MultiValue } from "react-select";
import _ from "lodash";
import FormikMultiSelect from "../../../../shared-components/FormikFormHandlers/FormikMultiSelect";

import monozlogo from "../../../../assets/Images/logo--white.png";
import monozlogoMobile from "../../../../assets/Images/monoZ_Black.png";
import RoutesConstants from "../../../../constants/routes.constant";
import { useHistory } from "react-router-dom";
import { IAddCustomer } from "../../../../models/forms/IAddCustomerManagement";
import FormikFormInputAddress from "../../../../shared-components/FormikFormHandlers/FormikFormInputAddress";
import FormikFormInputMobileNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import { ShowHidePasswordButton } from "../../../../shared-components/Grid/custom-grid-formatter";
import {
  EditCustomerProfileSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import ChangeCustomerPassword from "../components/change-customer-password";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import ChangeCustomerEmail from "../components/change-customer-email";
import ChangeCustomerPhone1 from "../components/change-customer-phone1";
import ChangeCustomerPhone2 from "../components/change-customer-phone2";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import {
  getDaysArrayForMonth,
  getMonthsArray,
  getYearsArray,
} from "../../../../helpers/date-time-helper";
import { observer } from "mobx-react";
import { FormObserver } from "../../../../shared-components/address/form-observer-customer-profile";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  getCustomerLoading: boolean;
  initialValues: any;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
}

const ProfileForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
  } = props;

  const history = useHistory();

  const { storeManagementStore } = useStore();
  const { GetPrefectureUsingPostalCode, prefectureByZipCode } =
    storeManagementStore;

  const [showPassword, setShowPassword] = useState(false);
  const [postalCode, setPostalCode] = useState<string>("");
  const [prefectureName, setPrefectureName] = useState<string>("");
  const [address12, setAddress12] = useState<string>("");
  const [showChangePasswordPopup, setShowChangePasswordPopup] = useState(false);
  const [showChangeEmailPopup, setShowChangeEmailPopup] = useState(false);
  const [showChangePhone1Popup, setShowChangePhone1Popup] = useState(false);
  const [showChangePhone2Popup, setShowChangePhone2Popup] = useState(false);
  const [daysInMonth, setDaysInMonth] = useState<number[]>([]);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => currentYear - i);
  // Generate months 1-12
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };
  useEffect(() => {
    if (props.initialValues?.DobYear && props.initialValues?.DobMonth) {
      const daysCount = new Date(
        Number(props.initialValues.DobYear),
        Number(props.initialValues.DobMonth),
        0
      ).getDate();

      const daysArray = Array.from({ length: daysCount }, (_, i) => i + 1);
      setDaysInMonth(daysArray);
    }
  }, [props.initialValues]);

  useEffect(() => {
    if (postalCode && postalCode?.length == 7) {
      GetPrefectureUsingPostalCode(postalCode);
    }
  }, [postalCode]);

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_your_account?"
          )}
        />
      )}
      {/* Change Password Modal */}
      {showChangePasswordPopup && (
        <ChangeCustomerPassword
          id={props.id}
          modalClosed={() => setShowChangePasswordPopup(false)}
        />
      )}

      {showChangeEmailPopup && (
        <ChangeCustomerEmail
          id={props.id}
          modalClosed={() => setShowChangeEmailPopup(false)}
        />
      )}

      {showChangePhone1Popup && (
        <ChangeCustomerPhone1
          id={props.id}
          modalClosed={() => setShowChangePhone1Popup(false)}
        />
      )}

      {showChangePhone2Popup && (
        <ChangeCustomerPhone2
          id={props.id}
          modalClosed={() => setShowChangePhone2Popup(false)}
        />
      )}

      <Formik
        initialValues={props.initialValues}
        enableReinitialize={true}
        validationSchema={EditCustomerProfileSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values, { resetForm }) => {
          const customerData = {
            FirstName: values.FirstName || null,
            LastName: values.LastName || null,
            FirstNameKana: values.FirstNameKana || null,
            LastNameKana: values.LastNameKana || null,
            Dob:
              values.DobDate &&
              values.DobMonth &&
              values.DobYear &&
              values.DobDate > 0 &&
              values.DobMonth > 0 &&
              values.DobYear > 0
                ? `${values.DobYear}-${values.DobMonth}-${values.DobDate}`
                : null,
            Gender: values.Gender ?? null,
            Phone1: toHalfWidth(values.Phone1) || null,
            Email: values.Email ? values.Email.toLowerCase() : null,
            PostalCode: toHalfWidth(values.PostalCode),
            Address: values.Address || null,
            PrefectureName: values.PrefectureName || null,
            DianaCode: values.DianaCode || null,
            AppMemberShipCode: values.AppMemberShipCode || null,
            Referer: values.Referer > 0 ? values.Referer : null,
            IsBlocked: values.IsBlocked,
            Memo: values.Memo || null,
            DmDshs: values.DmDshs === "true",
            Password: null,
            CustomerType: props.initialValues.CustomerType,
            RegisteredBy: values?.RegisteredBy,
            StoreId: values.StoreId ?? null,
            UserId: values.UserId ?? null,
            Status: values.Status,
            IsUpdated: false,
            UpdateCustomerMotiveDetails:[]
          };
          props.submitHandler({...customerData});
          resetForm();
        }}
      >
        {({ setFieldValue, values, errors }) => {
          return (
            <Form className="profile_form">
              {props.getCustomerLoading ? (
                <FormLoader loading={props.getCustomerLoading} />
              ) : null}
              <div className="display">
                <div className="logoregister">
                  {/* <figure>
                                        <img src={monozlogo} alt="logo" className="img-fluid"></img>
                                    </figure> */}
                </div>
              </div>
             <FormObserver
                prefectureByZipCode={prefectureByZipCode}
                initialPostalCode={props.initialValues.PostalCode}
                daysInMonth={daysInMonth}
                setDaysInMonth={setDaysInMonth}
              />
              {/* Account Details */}
              <h3>{formatMessage("account_details")}</h3>
              <div className="row g-0 profile_wrapper">
                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={true}
                    label="email"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2 editable_fields">
                  <Field
                    className="form-control"
                    name="Email"
                    type="email"
                    autoComplete="new-email"
                    placeholder="enter_email"
                    validcharacter={["@", "#", "$", "-", "_", "."]}
                    as={FormikFormInput}
                    disabled={props.id > 0 ? true : false}
                  />
                  {props.id > 0 && (
                    <button
                      className="btn btn-sm btn-primary ms-2"
                      type="button"
                      onClick={() => {
                        setShowChangeEmailPopup(true);
                      }}
                    >
                      {formatMessage("change_profile_btn")}
                    </button>
                  )}
                </div>
                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={true}
                    label="password"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2 editable_fields">
                  <Field
                    className="form-control "
                    name="Password"
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="enter_new_password"
                    invalidcharacter={[" "]}
                    validcharacter={[
                      "~",
                      "`",
                      "!",
                      "@",
                      "#",
                      "$",
                      "%",
                      "^",
                      "&",
                      "*",
                      "(",
                      ")",
                      "_",
                      "-",
                      "+",
                      "=",
                      "{",
                      "[",
                      "}",
                      "]",
                      "|",
                      "\\",
                      ":",
                      ";",
                      "'",
                      '"',
                      "<",
                      ",",
                      ">",
                      ".",
                      "?",
                      "/",
                    ]}
                    as={FormikFormInput}
                    disabled={props.id > 0 ? true : false}
                  />
                  {/* <ShowHidePasswordButton
                                                showHidePassword={showPassword}
                                            setShowHidePassword={setShowPassword}
                                                                    /> */}
                  {/* Change Password Button */}
                  {props.id > 0 && (
                    <button
                      className="btn btn-sm btn-primary ms-2"
                      type="button"
                      onClick={() => {
                        setShowChangePasswordPopup(true);
                      }}
                    >
                      {formatMessage("change_profile_btn")}
                    </button>
                  )}
                </div>

                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={true}
                    label="phone1"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2 editable_fields">
                  <Field
                    name="Phone1"
                    type="text"
                    className="form-control"
                    placeholder="enter_mobile_number"
                    regex={integerRegex}
                    as={FormikFormInputMobileNumber}
                    disabled={props.id > 0 ? true : false}
                  />
                  {props.id > 0 && (
                    <button
                      className="btn btn-sm btn-primary ms-2"
                      type="button"
                      onClick={() => {
                        setShowChangePhone1Popup(true);
                      }}
                    >
                      {formatMessage("change_profile_btn")}
                    </button>
                  )}
                </div>

                {/* <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={false}
                    label="phone2"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2 editable_fields">
                  <Field
                    name="Phone2"
                    type="text"
                    className="form-control"
                    placeholder="enter_mobile_number"
                    regex={integerRegex}
                    as={FormikFormInputMobileNumber}
                    // disabled={props.id > 0 ? true : false}
                    disabled={true}
                  />
                  <button
                    className="btn btn-sm btn-primary ms-2"
                    type="button"
                    onClick={() => setShowChangePhone2Popup(true)}
                  >
                    {props.initialValues.Phone2
                      ? formatMessage("change_profile_btn")
                      : formatMessage("add")}
                  </button>
                </div> */}
              </div>

              {/* Personal Details */}
              <h3>{formatMessage("personal_details")}</h3>
              <div className="row g-0 profile_wrapper">
                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label className="label-style" required={true} label="name" />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2">
                  <div className="row g-0">
                    <div className="col-6 p-sm-3 p-1">
                      <Label required={false} label="last_name" />
                      <Field
                        name="LastName"
                        type="text"
                        className="form-control"
                        placeholder="enter_last_name"
                        as={FormikFormInput}
                      />
                    </div>
                    <div className="col-6 p-sm-3 p-1">
                      <Label required={false} label="first_name" />
                      <Field
                        name="FirstName"
                        type="text"
                        className="form-control"
                        placeholder="enter_first_name"
                        as={FormikFormInput}
                      />
                    </div>
                  </div>

                  <div className="row g-0">
                    <div className="col-6 p-sm-3 p-1">
                      <Label required={true} label="last_name_kana" />
                      <Field
                        name="LastNameKana"
                        type="text"
                        className="form-control"
                        placeholder="last_name_kana"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                      />
                    </div>
                    <div className="col-6 p-sm-3 p-1">
                      <Label required={true} label="first_name_kana" />
                      <Field
                        name="FirstNameKana"
                        type="text"
                        className="form-control"
                        placeholder="first_name_kana"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={false}
                    label="gender"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2">
                  <Field
                    className="form-check-input"
                    name="Gender"
                    type="radio"
                    value="0"
                    id="male"
                  />
                  <label className="form-check-label me-4" htmlFor="male">
                    <FormattedMessage id="male" />
                  </label>
                  <Field
                    className="form-check-input"
                    name="Gender"
                    type="radio"
                    value="1"
                    id="female"
                  />
                  <label className="form-check-label me-4" htmlFor="female">
                    <FormattedMessage id="female" />
                  </label>
                  <Field
                    className="form-check-input"
                    name="Gender"
                    type="radio"
                    value="2"
                    id="not_set"
                  />
                  <label className="form-check-label me-4" htmlFor="not_set">
                    <FormattedMessage id="not_set" />
                  </label>
                </div>

                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={false}
                    label="date_of_birth"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2">
                  <div className="d-flex gap-2">
                    <div className="form-group">
                      <Field
                        className="form-control"
                        name="DobYear"
                        options={getYearsArray()}
                        as={FormikFormSelect}
                        showErrorField={false}
                      />
                    </div>
                    <div className="form-group">
                      <Field
                        className="form-control"
                        name="DobMonth"
                        options={getMonthsArray()}
                        as={FormikFormSelect}
                        showErrorField={false}
                      />
                    </div>
                    <div className="form-group">
                      <Field
                        className="form-control"
                        name="DobDate"
                        options={getDaysArrayForMonth(
                          values.DobYear,
                          values.DobMonth
                        )}
                        as={FormikFormSelect}
                        showErrorField={false}
                      />
                    </div>
                  </div>
                  {errors.DobYear && (
                    <div className="text-danger errorMessage">
                      {typeof errors.DobYear === "string" ? (
                        formatMessage(errors.DobYear)
                      ) : (
                        <></>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label
                    className="label-style"
                    required={false}
                    label="address"
                  />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2">
                  <div className="row g-1">
                    <div className="col-12 postal-code-width mb-3">
                      <Label required={false} label="postal_code" />
                      <Field
                        className="form-control"
                        name="PostalCode"
                        type="text"
                        placeholder="enter_post_code"
                        regex={integerNumJpRegex}
                        setState={setPostalCode}
                        as={FormikFormInputForRegexCheck}
                      />
                    </div>
                    <div className="col-12 next-postal-code-width">
                      <Label required={false} label="address" />
                      <Field
                        className="form-control"
                        name="Address"
                        type="text"
                        placeholder="enter_address"
                        prefectureName={values.PrefectureName}
                        validcharacter={["@", "#", "$", "-", "_", ".", "/"]}
                        as={FormikFormInputAddress}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* Other Details */}
              <h3>{formatMessage("other_details")}</h3>

              <div className="row g-0 profile_wrapper">
                <div className="col-12 col-md-4 bg-light border-bottom p-sm-3 p-2">
                  <Label className="label-style" required={false} label="dm" />
                </div>
                <div className="col-12 col-md-8 border-start border-bottomp-sm-3 p-2">
                  <div className="row">
                    <div className="col-6">
                      <Field
                        className="form-check-input"
                        name="DmDshs"
                        type="radio"
                        value="true"
                        id="yes"
                      />
                      <label className="form-check-label" htmlFor="yes">
                        <FormattedMessage id="yes" />
                      </label>
                    </div>
                    <div className="col-6">
                      <Field
                        className="form-check-input"
                        name="DmDshs"
                        type="radio"
                        value="false"
                        id="no"
                      />
                      <label className="form-check-label" htmlFor="no">
                        <FormattedMessage id="no" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 text-center position-relative d-flex align-items-center justify-content-center">
                <Button type="submit" className=" w-25">
                  <i className="bi bi-floppy2 me-2"></i>
                  {formatMessage("button_save")}
                </Button>

                {/* <Button
                  variant="link"
                  type="button"
                  className="text-danger small p-0 delete_acc_btn"
                  onClick={handleDelete}
                  disabled={props.getCustomerLoading}
                >
                  <FormattedMessage id="deleting_an_account" />
                </Button> */}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default DeletePopupHOC(observer(ProfileForm));
